import React, { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UploadCloud02, DownloadCloud01 } from '../../../../assets';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { BarcodeRowResult } from '../../../../app/services/api/orderManagementClient';

type Props = {
  open: boolean;
  onClose: () => void;
  onFileUpload: (file: File) => Promise<BarcodeRowResult[] | undefined>;
  onDownloadTemplate: () => void;
};

const BulkAddBarcodeMappingDialog: FC<Props> = ({ open, onClose, onFileUpload, onDownloadTemplate }) => {
  const { t } = useTranslation('barcodeMapping');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadResults, setUploadResults] = useState<BarcodeRowResult[] | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedFile(null);
      setFileError(null);
    }
  }, [open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const fileName = file.name.toLowerCase();

      if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls') || fileName.endsWith('.csv')) {
        setSelectedFile(file);
        setFileError(null);
      } else {
        setSelectedFile(null);
        setFileError(t('invalidFileType'));
      }
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadResults(null);
    onClose();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setIsLoading(true);
    setUploadResults(null);
    try {
      const results = await onFileUpload(selectedFile);
      if (results) {
        setUploadResults(results);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>
        <Box display="flex"
          justifyContent="space-between"
          alignItems="center">
          <Typography>{t('bulkAddTitle')}</Typography>
          <Button
            variant='primary'
            size="lg"
            startIcon={<DownloadCloud01 />}
            onClick={onDownloadTemplate}>
            {t('downloadTemplate')}
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          minHeight="300px"
        >
          {!selectedFile && !fileError && <>
            <UploadCloud02 />
            <Typography
              sx={{ mt: 2 }}>
              {t('bulkAddInstructions')}
            </Typography>
            <Typography
              sx={{ color: 'gray', mt: 1, mb: 4 }}>
              {t('uploadHint', { formats: '.xls, .xlsx, .csv' })}
            </Typography>
          </>}
          <input
            type="file"
            accept=".xls,.xlsx,.csv"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="file-upload"
          />
          <label htmlFor="file-upload">
            <Button
              variant="primary"
              component="span"
              size="lg"
              startIcon={<UploadCloud02 />}
            >
              {t('selectFile')}
            </Button>
          </label>
          {fileError && (
            <Typography 
              color="error" 
              sx={{ mt: 4 }}
            >
              {fileError}
            </Typography>
          )}
          {selectedFile && (
            <Typography sx={{ mt: 2 }}>
              {t('selectedFile')}: {selectedFile.name}
            </Typography>
          )}
          {isLoading && <CircularProgress sx={{ mt: 2 }} />}
          {uploadResults && (
            <Box
              mt={2}
              p={1}
              border="1px solid #ccc"
              borderRadius="8px"
              maxHeight="150px"
              overflow="auto"
              width="100%"
            >
              {uploadResults.map((result, index) => (
                <Box key={index}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  mb={1}>
                  {result.IsSuccess ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <CancelOutlinedIcon color="error" />
                  )}
                  <Typography variant="textSM">
                    {t('row')} {result.RowNumber}: {result.Message}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="secondary"
          size="lg"
          onClick={handleClose}
          disabled={isLoading}
        >
          {t('cancel')}
        </Button>
        <Button
          variant="primary"
          size="lg"
          startIcon={<UploadCloud02 />}
          onClick={handleUpload}
          disabled={!selectedFile || isLoading || !!fileError}
        >
          {t('upload')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkAddBarcodeMappingDialog;